import matchSorter, { rankings } from 'match-sorter';
import moment from 'moment-timezone';
import { Filter } from 'react-table';

const formatToYYYYMMDD = (date: Date): string => moment(date).format('YYYY-MM-DD');

export type FilterKey = string | ((item: any) => string);

const readFilterKey = (item: any, key: FilterKey) =>
  typeof key === 'function' ? key(item) : item[key];

export const filterMethodContainsOrDoesntContain = (filter: Filter, rows: any[], key: FilterKey) => {
  const value = filter.value;
  const notTextSymbol = '-'; // tried to follow google https://ahrefs.com/blog/google-advanced-search-operators/
  if (typeof value === 'string' && value.length > 1 && value.charAt(0) === notTextSymbol) {
    const valueExludingExclamation = value.substring(1);
    const valuesToExclude
      = matchSorter(rows, valueExludingExclamation, { threshold: rankings.CONTAINS, keys: [key] });
    return rows.filter((item: any) => !valuesToExclude.includes(item));
  }

  return matchSorter(rows, filter.value, { threshold: rankings.CONTAINS, keys: [key] });
};

const rowMatchesTerms = (item: any, key: FilterKey, terms: string[]) => {
  const itemValue = String(readFilterKey(item, key) ?? '').toLowerCase();
  return terms.some((term: string) => itemValue.includes(term));
};

export const filterMethodContainsOrDoesntContainWithListSupport = (filter: Filter, rows: any[], key: FilterKey) => {
  const value = filter.value;
  const notTextSymbol = '-';

  if (typeof value !== 'string') {
    return matchSorter(rows, filter.value, { threshold: rankings.CONTAINS, keys: [key] });
  }

  const hasComma = value.includes(',');

  if (hasComma && value.length > 1 && value.charAt(0) === notTextSymbol) {
    const terms = value.substring(1)
      .split(',')
      .map((s: string) => s.trim().toLowerCase())
      .filter((s: string) => s.length > 0);

    return rows.filter((item: any) => !rowMatchesTerms(item, key, terms));
  }

  if (hasComma) {
    const terms = value
      .split(',')
      .map((s: string) => s.trim().toLowerCase())
      .filter((s: string) => s.length > 0);

    return rows.filter((item: any) => rowMatchesTerms(item, key, terms));
  }

  return filterMethodContainsOrDoesntContain(filter, rows, key);
};

export const filterMethodOnStringArray = (filterValue: string, rows: any[]) => {
  if (filterValue === '*') {
    return Array.isArray(rows) && rows.length > 0;
  }
  if (filterValue === '^') {
    return !rows || (Array.isArray(rows) && rows.length === 0);
  }
  const temp = matchSorter(rows, filterValue);
  return temp.length >= 1;
};

export const exactTextMatchFilter = (filters: Filter, row: any) => {
  let hasFoundMatch = false;
  const isEmpty = !row[filters.id];
  for (const filter of filters.value) {
    if (filter === 'Unassigned' && isEmpty) {
      hasFoundMatch = true;
    }
    if (filter === row[filters.id]) {
      hasFoundMatch = true;
    }
  }
  return hasFoundMatch;
};

export const getDatesForPredefinedDayFilter = (filter: any) => {
  if (filter?.clearFilter) {
    return {
      startDate: undefined,
      endDate: undefined,
    };
  }
  if (filter?.customFilter && !filter?.nextDays && !filter?.lastDays) {
    if ((filter.startDate && filter.endDate) || (!filter.startDate && !filter.endDate)) {
      const startDate = filter.startDate ? moment(filter.startDate) : moment();
      const endDate = filter.endDate ? moment(filter.endDate) : moment();
      return {
        startDate: startDate.startOf('day').toDate(),
        endDate: endDate.endOf('day').toDate(),
      };
    }
  }
  if (filter?.nextDays && !filter?.lastDays && !filter.customFilter) {
    return {
      startDate: moment().startOf('day').toDate(),
      endDate: moment().add(filter.nextDays, 'day').endOf('day').toDate(),
    };
  }
  if (filter?.lastDays && !filter?.nextDays && !filter?.customFilter) {
    return {
      startDate: moment().subtract(filter.lastDays, 'day').startOf('day').toDate(),
      endDate: moment().endOf('day').toDate(),
    };
  }
  return;
};

export const filterMethodDateRange = (
  filter: { value: unknown },
  dateValue: unknown,
  timezone: string,
): boolean => {
  const filterValue = filter.value as {
    startDate?: Date;
    endDate?: Date;
  };
  let { startDate, endDate } = filterValue;
  const predefinedDayFilterDate = getDatesForPredefinedDayFilter(filter.value);
  if (predefinedDayFilterDate) {
    startDate = predefinedDayFilterDate.startDate;
    endDate = predefinedDayFilterDate.endDate;
  }
  if (startDate === undefined && endDate === undefined) {
    return true;
  }
  if (!dateValue) {
    return false;
  }
  const rowDate = moment(moment.tz(dateValue, timezone).format('YYYY-MM-DD'));
  if (startDate && rowDate.isBefore(formatToYYYYMMDD(startDate))) {
    return false;
  }
  if (endDate && rowDate.isAfter(formatToYYYYMMDD(endDate))) {
    return false;
  }
  return true;
};
